import { motion, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Zap, Brain, MousePointerClick, FileText, ShieldCheck, ArrowRight } from "lucide-react";

const features = [
  { icon: Zap, title: "Automatización real", desc: "No promesas vacías. Procesos que se ejecutan solos. Tu negocio trabaja mientras duermes.", accent: "#00E5FF", tag: "CORE" },
  { icon: Brain, title: "IA que habla tu idioma", desc: "Diseñada para el día a día de una empresa real española. Sin tecnicismos. Sin barreras. Sin excusas.", accent: "#7B5EA7", tag: "INTELIGENCIA" },
  { icon: MousePointerClick, title: "Sin curva de aprendizaje", desc: "Tecnología que se adapta a ti, no al revés. Intuitiva desde el primer segundo.", accent: "#F5A623", tag: "SIMPLICIDAD" },
  { icon: FileText, title: "Facturación ágil", desc: "Agiliza tu facturación de principio a fin. Genera, envía y controla todas tus facturas sin esfuerzo.", accent: "#00E5FF", tag: "FACTURACIÓN" },
  { icon: ShieldCheck, title: "Cobros e impagos bajo control", desc: "Aumenta tus cobros, reduce impagos y recobros. El dinero que te deben, de vuelta a tu cuenta.", accent: "#F5A623", tag: "RECOBROS" },
];

function FeatureCard3D({ feature, index, isInView }) {
  const cardRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 180, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 180, damping: 18 });

  function onMove(e) {
    if (!cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  }

  const Icon = feature.icon;

  return (
    <motion.div
      ref={cardRef}
      data-testid={`feature-card-${index}`}
      initial={{ opacity: 0, y: 100, rotateX: 25 }}
      animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
      transition={{ duration: 0.9, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      style={{ rotateX: hovered ? rx : 0, rotateY: hovered ? ry : 0, transformStyle: "preserve-3d" }}
      onMouseMove={onMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { mx.set(0); my.set(0); setHovered(false); }}
      className="group relative rounded-2xl glass-card-strong p-7 md:p-9 overflow-hidden cursor-default transition-all duration-300 hover:border-white/15"
    >
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `radial-gradient(ellipse at 50% -20%, ${feature.accent}15 0%, transparent 60%)` }} />
      <div className="absolute top-0 left-8 right-8 h-px opacity-0 group-hover:opacity-100 transition-all duration-500" style={{ background: `linear-gradient(90deg, transparent, ${feature.accent}80, transparent)` }} />

      <div className="relative z-10" style={{ transform: "translateZ(30px)" }}>
        <span className="inline-block text-[9px] font-black tracking-[0.3em] px-3 py-1 rounded-full mb-5 border" style={{ color: feature.accent, borderColor: `${feature.accent}25`, background: `${feature.accent}08` }}>
          {feature.tag}
        </span>

        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110" style={{ background: `${feature.accent}0D`, border: `1px solid ${feature.accent}20`, boxShadow: hovered ? `0 0 40px ${feature.accent}25` : "none" }}>
          <Icon className="w-6 h-6" style={{ color: feature.accent }} />
        </div>

        <h3 className="text-xl font-bold text-white mb-2 tracking-tight">{feature.title}</h3>
        <p className="text-[#6B7FD7] text-sm leading-relaxed mb-5">{feature.desc}</p>

        <div className="flex items-center gap-2 text-white/25 group-hover:text-white/60 transition-colors duration-300">
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase">Próximamente</span>
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-2 transition-transform duration-300" />
        </div>
      </div>
    </motion.div>
  );
}

export default function FeaturesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section data-testid="features-section" ref={ref} className="relative py-28 md:py-40 px-6 md:px-12">
      <motion.div initial={{ opacity: 0, y: 50 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.9 }} className="text-center mb-16 md:mb-24 max-w-3xl mx-auto">
        <p className="text-[#F5A623] text-xs font-bold tracking-[0.35em] uppercase mb-5">La solución del futuro</p>
        <h2 data-testid="features-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight text-center">
          Tecnología que{" "}
          <span className="bg-gradient-to-r from-[#F5A623] to-[#00E5FF] bg-clip-text text-transparent">trabaja por ti.</span>
        </h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-[#6B7FD7] text-base md:text-lg mt-5 max-w-xl mx-auto text-center leading-relaxed"
        >
          Viene del futuro. Soluciones a medida, personalizadas para tu negocio. Desde la automatización
          total hasta el control de facturación, cobros e impagos.
        </motion.p>
      </motion.div>

      {/* Top row: 3 cards */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 mb-6 md:mb-7" style={{ perspective: "1200px" }}>
        {features.slice(0, 3).map((f, i) => (
          <FeatureCard3D key={i} feature={f} index={i} isInView={isInView} />
        ))}
      </div>

      {/* Bottom row: 2 cards centered */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7" style={{ perspective: "1200px" }}>
        {features.slice(3).map((f, i) => (
          <FeatureCard3D key={i + 3} feature={f} index={i + 3} isInView={isInView} />
        ))}
      </div>
    </section>
  );
}
